import * as MSAL from '@azure/msal-browser'
import config from '../config.js'

const msal = new MSAL.PublicClientApplication(config.msal)

/**
 * Fetch an access token for SharePoint
 */
export function getSpAccessToken (userData) {
  const url = new URL(userData.sc_root)
  const scopes = [`${url.protocol}//${url.host}/.default`]
  const authority = userData.alternative_token_issuer
    ? `https://login.microsoftonline.com/${userData.alternative_token_issuer}`
    : undefined
  return getAccessToken(scopes, userData.email, authority)
}

/**
 * Fetch an access token for Graph API — Outlook operations (home tenant).
 * Do NOT use this for SharePoint file access in cross-tenant setups.
 */
export function getGraphAccessToken (userData) {
  const scopes = ['mail.read', 'mail.send']
  return getAccessToken(scopes, userData.email)
}

/**
 * Fetch a Graph API token scoped to the SharePoint data tenant.
 * In cross-tenant setups the user's home tenant differs from the tenant
 * where SharePoint data lives (alternative_token_issuer). Using the wrong
 * authority here results in a 403 on the PDF conversion endpoint.
 */
export function getSpGraphAccessToken (userData) {
  const authority = userData.alternative_token_issuer
    ? `https://login.microsoftonline.com/${userData.alternative_token_issuer}`
    : undefined
  const scopes = ['sites.manage.all']
  return getAccessToken(scopes, userData.email, authority)
}

/**
 * Common access token function.
 */
async function getAccessToken (scopes, email, authority) {
  const account = msal.getAccountByUsername(email)
  const request = { scopes, account, ...(authority && { authority }) }
  try {
    const result = await msal.acquireTokenSilent(request)
    return result.accessToken
  } catch (err) {
    if (err?.errorCode === 'interaction_in_progress') {
      // In redirect context: wait for the redirect to settle then retry silently.
      // In popup context: handleRedirectPromise resolves immediately to null and
      // the retry will also fail, so fall through to the interactive fallback.
      try {
        await msal.handleRedirectPromise().catch(() => {})
        const retry = await msal.acquireTokenSilent(request)
        return retry.accessToken
      } catch {
        // fall through to interactive fallback below
      }
    }
    console.error('Failed to get access token silently', err)
    if (window.self !== window.top) {
      // iframe (Office Online / O365 web) — use popup
      console.info('Trying acquireTokenPopup (O365 web context)')
      const result = await msal.acquireTokenPopup(request)
      return result.accessToken
    } else {
      // Desktop Office add-in — use redirect
      console.info('Trying acquireTokenRedirect (desktop context)')
      await msal.acquireTokenRedirect(request)
    }
  }
}

export default msal
